import { EmailSend } from '../email/EmailSend';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import {
  Box,
  Button,
  Card,
  GlobalStyles,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { DEFAULT_BRAND } from '../../design/color';
import { api, unwrap, type Schemas } from '../../api/client';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime, money, type FormatSettings } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { useShop, type ShopSettings } from '../../shop/ShopProvider';
import {
  contactKeys,
  KIND,
  presetRange,
  todayIn,
  type ContactKind,
  type Ledger,
  type PartyBalance,
} from './model';
import { DateRangeBar } from './ui';
import { useShopLogo } from '../../lib/images';
import { poweredByText } from '../../components/PoweredBy';
import { WhatsappSend } from '../whatsapp';

type Statement = Schemas['Statement'];

const PRINT_CSS = {
  '.sm-print-only': { display: 'none' },
  '@media print': {
    '@page': {
      size: 'A4',
      margin: '14mm 12mm 16mm',
      '@bottom-right': {
        content: '"Page " counter(page) " of " counter(pages)',
        fontSize: '8pt',
        color: '#667085',
      },
    },
    // Light paper even in dark mode (the dark scheme paints the page margins dark).
    ':root': { colorScheme: 'light !important' },
    'html, body': { background: '#fff !important', height: 'auto' },
    '#root': { display: 'none !important' },
    '.sm-print-only': { display: 'block !important' },
  },
} as const;

/** /customers/:id/statement — A4 statement for a period, printable with the browser. */
export function StatementPage({ kind }: { kind: ContactKind }) {
  const { id = '' } = useParams();
  const k = KIND[kind];
  const navigate = useNavigate();
  const { format, settings } = useShop();
  const logo = useShopLogo();
  const [params, setParams] = useSearchParams();
  const today = todayIn(format.timeZone);
  const fallback = presetRange('thisMonth', today);
  const from = params.get('from') ?? fallback.from;
  const to = params.get('to') ?? fallback.to;
  const ledgerParam = params.get('ledger') as Ledger | null;
  const ledger: Ledger =
    ledgerParam === 'RECEIVABLE' || ledgerParam === 'PAYABLE' ? ledgerParam : k.ledger;

  const query = { ledger, from: from || undefined, to: to || undefined };
  const data = useQuery({
    queryKey: contactKeys.statement(id, query),
    queryFn: () => unwrap(api.GET('/contacts/{id}/statement', { params: { path: { id }, query } })),
    placeholderData: keepPreviousData,
    enabled: !!id,
  });
  const contact = useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id } } })),
    enabled: !!id,
  });
  const both = !!contact.data && contact.data.isCustomer && contact.data.isSupplier;

  const set = (next: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [key, v] of Object.entries(next)) p.set(key, v);
    setParams(p, { replace: true });
  };

  return (
    <>
      <GlobalStyles styles={PRINT_CSS} />
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { lg: 'flex-end' }, mb: 3 }}
      >
        <Box>
          <Button
            size="small"
            color="inherit"
            startIcon={<ArrowBackRounded />}
            onClick={() => void navigate(`${k.base}/${id}`)}
            sx={{ mb: 1, ml: -1, color: 'text.secondary' }}
          >
            {data.data?.contact.name ?? contact.data?.name ?? 'Back'}
          </Button>
          <Typography variant="h3">Statement</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Choose a period, check the preview, then print or save as PDF.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' } }}
        >
          {both && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={ledger}
              onChange={(_, v: Ledger | null) => v && set({ ledger: v })}
            >
              <ToggleButton value="RECEIVABLE">Receivable</ToggleButton>
              <ToggleButton value="PAYABLE">Payable</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button
            variant="contained"
            startIcon={<PrintRounded />}
            disabled={!data.data}
            onClick={() => window.print()}
          >
            Print
          </Button>
          {data.data && (
            <>
              <WhatsappSend
                label="Send ledger"
                defaultOpen={params.get('whatsapp') === '1'}
                target={{
                  documentType: 'contact_ledger',
                  documentId: id,
                  documentRef: 'Statement',
                  contact: {
                    id,
                    name: data.data.contact.name,
                    phone: contact.data?.phone,
                    isWalkIn: false,
                  },
                  vars: {
                    name: data.data.contact.name,
                    amount: signedLabel(data.data.closingBalance, format),
                  },
                  pdf: {
                    fileName: `Statement ${data.data.contact.name}.pdf`,
                    paper: 'a4',
                    render: () => (
                      <StatementSheet
                        statement={data.data}
                        settings={settings}
                        format={format}
                        logoSrc={logo}
                      />
                    ),
                  },
                }}
              />
              <EmailSend
                label="Email ledger"
                defaultOpen={params.get('email') === '1'}
                target={{
                  documentType: 'contact_ledger',
                  documentId: id,
                  documentRef: 'Statement',
                  contact: {
                    id,
                    name: data.data.contact.name,
                    phone: contact.data?.phone,
                    isWalkIn: false,
                  },
                  vars: {
                    name: data.data.contact.name,
                    amount: signedLabel(data.data.closingBalance, format),
                  },
                  pdf: {
                    fileName: `Statement ${data.data.contact.name}.pdf`,
                    paper: 'a4',
                    render: () => (
                      <StatementSheet
                        statement={data.data}
                        settings={settings}
                        format={format}
                        logoSrc={logo}
                      />
                    ),
                  },
                }}
              />
            </>
          )}
        </Stack>
      </Stack>
      <Card sx={{ p: 2, mb: 3 }}>
        <DateRangeBar
          from={from}
          to={to}
          today={today}
          onChange={(r) => set({ from: r.from, to: r.to })}
        />
      </Card>

      {data.error ? (
        <Card>
          <EmptyState
            icon={<ReceiptLongRounded />}
            title="Couldn’t build the statement"
            body={errorMessage(data.error)}
            action={{ label: 'Try again', onClick: () => void data.refetch() }}
          />
        </Card>
      ) : (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          <Box
            sx={{
              width: '210mm',
              minHeight: '297mm',
              mx: 'auto',
              bgcolor: '#fff',
              color: '#101828',
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(16,24,40,0.06), 0 24px 48px -16px rgba(16,24,40,0.18)',
              p: '14mm 12mm',
              opacity: data.isFetching && data.data ? 0.6 : 1,
              transition: 'opacity 150ms',
            }}
          >
            {data.data ? (
              <StatementSheet
                statement={data.data}
                settings={settings}
                format={format}
                logoSrc={logo}
              />
            ) : (
              <SheetSkeleton />
            )}
          </Box>
        </Box>
      )}

      {data.data &&
        createPortal(
          <Box className="sm-print-only" sx={{ color: '#101828', bgcolor: '#fff' }}>
            <StatementSheet
              statement={data.data}
              settings={settings}
              format={format}
              logoSrc={logo}
            />
          </Box>,
          document.body,
        )}
    </>
  );
}

function SheetSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Skeleton width={220} height={36} />
          <Skeleton width={180} />
          <Skeleton width={140} />
        </Box>
        <Box>
          <Skeleton width={160} height={36} />
          <Skeleton width={120} />
        </Box>
      </Stack>
      <Skeleton variant="rounded" height={110} />
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} height={26} />
      ))}
    </Stack>
  );
}

function signedLabel(b: PartyBalance, format: FormatSettings): string {
  if (b.direction === 'SETTLED') return money(0, format);
  return `${money(b.amount, format)} ${b.direction === 'OWED_TO_SHOP' ? 'Dr' : 'Cr'}`;
}

const cell = {
  py: '6px',
  px: '8px',
  borderBottom: '1px solid #EAECF0',
  fontSize: '9pt',
  textAlign: 'left',
  verticalAlign: 'top',
} as const;
const num = {
  ...cell,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
} as const;
const head = {
  ...cell,
  fontSize: '7.5pt',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#475467',
  borderBottom: '1.5px solid #101828',
  bgcolor: '#F7F8FA',
} as const;

/** The printable A4 statement: shop header, party block, summary, entries table, closing. */
export function StatementSheet({
  statement: s,
  settings,
  format,
  logoSrc = null,
}: {
  statement: Statement;
  /** The shop logo, already loaded (`useShopLogo`); the initial without one. */
  logoSrc?: string | null;
  settings: ShopSettings | null;
  format: FormatSettings;
}) {
  const fmt: FormatSettings = {
    ...format,
    currencyCode: s.currencyCode,
    currencyDecimals: s.currencyDecimals,
  };
  const shop = {
    name: settings?.name ?? s.shop.name,
    address: settings?.address ?? s.shop.address,
    phone: settings?.phone ?? s.shop.phone,
    email: settings?.email ?? s.shop.email,
    taxNumber: settings?.taxNumber ?? s.shop.taxNumber,
  };
  const accent = settings?.themeColor ?? DEFAULT_BRAND;
  const due = s.closingBalance;
  const dueLabel =
    due.direction === 'SETTLED'
      ? 'Settled'
      : s.ledger === 'RECEIVABLE'
        ? due.direction === 'OWED_TO_SHOP'
          ? 'Amount due to us'
          : 'Credit in your favour'
        : due.direction === 'OWED_BY_SHOP'
          ? 'Amount we owe you'
          : 'Amount you owe us';

  return (
    <Box sx={{ fontSize: '9.5pt', lineHeight: 1.45, '& *': { fontFamily: 'inherit' } }}>
      {/* Shop header */}
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt=""
              sx={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: accent,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: '15pt',
                printColorAdjust: 'exact',
                WebkitPrintColorAdjust: 'exact',
              }}
            >
              {shop.name.charAt(0).toUpperCase()}
            </Box>
          )}
          <Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: '15pt', letterSpacing: '-0.01em', color: '#101828' }}
            >
              {shop.name}
            </Typography>
            <Typography sx={{ fontSize: '8.5pt', color: '#475467' }}>
              {[shop.address, shop.phone, shop.email].filter(Boolean).join(' · ')}
            </Typography>
            {shop.taxNumber && (
              <Typography sx={{ fontSize: '8.5pt', color: '#475467' }}>
                Tax no. {shop.taxNumber}
              </Typography>
            )}
          </Box>
        </Stack>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{ fontWeight: 800, fontSize: '16pt', color: accent, letterSpacing: '-0.01em' }}
          >
            {s.title}
          </Typography>
          <Typography sx={{ fontSize: '8.5pt', color: '#475467' }}>
            {s.period.from ? businessDate(s.period.from, fmt) : 'Beginning'} –{' '}
            {businessDate(s.period.to, fmt)}
          </Typography>
          <Typography sx={{ fontSize: '8pt', color: '#667085' }}>
            Generated {dateTime(s.generatedAt, fmt)}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          height: '3px',
          bgcolor: accent,
          borderRadius: 2,
          my: 2.5,
          printColorAdjust: 'exact',
          WebkitPrintColorAdjust: 'exact',
        }}
      />

      {/* Party + summary */}
      <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 3, mb: 2.5 }}>
        <Box>
          <Typography
            sx={{ fontSize: '7.5pt', fontWeight: 700, letterSpacing: '0.08em', color: '#667085' }}
          >
            {s.ledger === 'RECEIVABLE' ? 'STATEMENT FOR' : 'SUPPLIER'}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '11.5pt', color: '#101828' }}>
            {s.contact.name}
          </Typography>
          {s.contact.address && (
            <Typography sx={{ fontSize: '9pt', color: '#344054', whiteSpace: 'pre-line' }}>
              {s.contact.address}
            </Typography>
          )}
          <Typography sx={{ fontSize: '9pt', color: '#344054' }}>
            {[s.contact.phone, s.contact.email].filter(Boolean).join(' · ')}
          </Typography>
          {s.contact.taxNumber && (
            <Typography sx={{ fontSize: '9pt', color: '#344054' }}>
              Tax no. {s.contact.taxNumber}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            minWidth: '64mm',
            border: '1px solid #EAECF0',
            borderRadius: '10px',
            overflow: 'hidden',
            fontSize: '9pt',
          }}
        >
          {[
            ['Opening balance', signedLabel(s.openingBalance, fmt)],
            ['Debits', money(s.totals.debit, fmt)],
            ['Credits', money(s.totals.credit, fmt)],
          ].map(([l, v]) => (
            <Stack
              key={l}
              direction="row"
              sx={{
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                borderBottom: '1px solid #EAECF0',
              }}
            >
              <span style={{ color: '#475467' }}>{l}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{v}</span>
            </Stack>
          ))}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: '#F7F8FA',
              printColorAdjust: 'exact',
              WebkitPrintColorAdjust: 'exact',
            }}
          >
            <Typography
              sx={{ fontSize: '7.5pt', fontWeight: 700, letterSpacing: '0.06em', color: '#475467' }}
            >
              {dueLabel.toUpperCase()}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '14pt',
                fontVariantNumeric: 'tabular-nums',
                color: '#101828',
              }}
            >
              {money(due.direction === 'SETTLED' ? 0 : due.amount, fmt)}
            </Typography>
          </Box>
        </Box>
      </Stack>

      {/* Entries */}
      <Box
        component="table"
        sx={{ width: '100%', borderCollapse: 'collapse', '& tr': { breakInside: 'avoid' } }}
      >
        <Box component="thead" sx={{ display: 'table-header-group' }}>
          <tr>
            <Box component="th" sx={{ ...head, width: '17%' }}>
              Date
            </Box>
            <Box component="th" sx={{ ...head, width: '24%' }}>
              Document
            </Box>
            <Box component="th" sx={head}>
              Details
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              Debit
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              Credit
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              Balance
            </Box>
          </tr>
        </Box>
        <tbody>
          <tr>
            <Box component="td" sx={cell}>
              {s.period.from ? businessDate(s.period.from, fmt) : '—'}
            </Box>
            <Box component="td" sx={{ ...cell, fontWeight: 600 }} colSpan={4}>
              Opening balance
            </Box>
            <Box component="td" sx={{ ...num, fontWeight: 600 }}>
              {signedLabel(s.openingBalance, fmt)}
            </Box>
          </tr>
          {s.entries.map((e) => (
            <tr key={e.lineId}>
              <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                {businessDate(e.entryDate, fmt)}
              </Box>
              <Box component="td" sx={cell}>
                <div style={{ fontWeight: 600 }}>{e.document.label}</div>
                {e.document.number && (
                  <div style={{ color: '#667085', fontSize: '8pt' }}>{e.document.number}</div>
                )}
              </Box>
              <Box component="td" sx={{ ...cell, color: '#475467' }}>
                {e.document.reference ?? e.description ?? ''}
                {e.status !== 'POSTED' && (
                  <span
                    style={{ color: '#B42318', marginLeft: 6, fontSize: '7.5pt', fontWeight: 700 }}
                  >
                    {e.status === 'REVERSED' ? 'REVERSED' : 'REVERSAL'}
                  </span>
                )}
              </Box>
              <Box component="td" sx={num}>
                {Number(e.debit) ? money(e.debit, fmt) : ''}
              </Box>
              <Box component="td" sx={num}>
                {Number(e.credit) ? money(e.credit, fmt) : ''}
              </Box>
              <Box component="td" sx={num}>
                {signedLabel(e.balance, fmt)}
              </Box>
            </tr>
          ))}
          {s.entries.length === 0 && (
            <tr>
              <Box
                component="td"
                colSpan={6}
                sx={{ ...cell, textAlign: 'center', color: '#667085', py: 3 }}
              >
                No transactions in this period.
              </Box>
            </tr>
          )}
          <tr>
            <Box
              component="td"
              sx={{ ...cell, borderBottom: '1.5px solid #101828', fontWeight: 700 }}
              colSpan={3}
            >
              Closing balance · {businessDate(s.period.to, fmt)}
            </Box>
            <Box
              component="td"
              sx={{ ...num, borderBottom: '1.5px solid #101828', fontWeight: 700 }}
            >
              {money(s.totals.debit, fmt)}
            </Box>
            <Box
              component="td"
              sx={{ ...num, borderBottom: '1.5px solid #101828', fontWeight: 700 }}
            >
              {money(s.totals.credit, fmt)}
            </Box>
            <Box
              component="td"
              sx={{ ...num, borderBottom: '1.5px solid #101828', fontWeight: 800 }}
            >
              {signedLabel(s.closingBalance, fmt)}
            </Box>
          </tr>
        </tbody>
      </Box>

      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', mt: 3, gap: 3, fontSize: '8pt', color: '#667085' }}
      >
        <Box sx={{ maxWidth: '60%' }}>
          Dr = {s.ledger === 'RECEIVABLE' ? 'you owe us' : 'you owe us (advance)'} · Cr ={' '}
          {s.ledger === 'RECEIVABLE' ? 'we owe you (credit)' : 'we owe you'}. Please let us know if
          your records differ.
        </Box>
        <Box sx={{ textAlign: 'right' }}>{shop.name}</Box>
      </Stack>
      <Box sx={{ mt: 1, textAlign: 'center', color: '#667085', fontSize: '7pt' }}>
        {poweredByText()}
      </Box>
    </Box>
  );
}
